/**
 * Interactive Walkthrough — deep-dive client explainer.
 *
 * 20 steps organized across 12 sub-chapters. Covers every major surface,
 * the 9-stage tracker, and the full Funding Center + Simulator + Credit
 * Report AI parser.
 *
 * Built on react-joyride v3 with a custom tooltip card, spotlight cutout,
 * animated bouncing arrow, chapter progress bar, canvas-confetti burst,
 * and Web-Audio duck-quack + celebration fanfare sound effects.
 *
 * IMPORTANT: This component MUST be mounted ONCE inside <BrowserRouter> but
 * outside <Routes> (see App.js). Mounting inside AppShell caused every route
 * change to unmount + remount the tour and force-redirect back to /portal.
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Joyride, EVENTS, STATUS, ACTIONS } from "react-joyride";
import confetti from "canvas-confetti";
import { Volume2, VolumeX, Mic, MicOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY_PREFIX = "cq_walkthrough_completed_v2_";
const RUNNING_EVENT = "cq:walkthrough:start";
const MUTE_KEY = "cq_wt_muted";
const VOICE_KEY = "cq_wt_voice_enabled";

/* ---------------------------------------------------------------- */
/* Step definitions — each step optionally declares a `path` — the   */
/* tour navigates the client there first. Steps with target=body     */
/* are "explainer" cards that don't highlight a UI element.          */
/* Steps are grouped by chapter for the progress bar UI.             */
/* ---------------------------------------------------------------- */
const STEPS = [
  // ============ CHAPTER 1: WELCOME + THE TRACKER ============
  {
    chapter: "Welcome",
    icon: "👋",
    path: "/portal",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Welcome to Credit Quack",
    content:
      "This 3-minute tour walks you through everything — your Credit Tracker, how disputes actually work, the Funding Center, the AI credit-report reader, the Simulator, and how you get help. Ready? Let's go.",
  },
  {
    chapter: "The Tracker",
    icon: "🗺️",
    path: "/portal",
    target: '[data-testid="pizza-tracker"]',
    placement: "bottom",
    disableBeacon: true,
    title: "Your Credit Tracker — 9 stages",
    content:
      "This is the map of your entire case. Nine stages: Getting Started → Credit Analysis → Disputes Submitted → Bureau Processing → Results Coming In → Credit Optimization → Funding Ready → Funding in Progress → Completed. Stages behind you turn green, your current stage glows amber. You'll get an in-app popup at key milestones (like Disputes Submitted) so you never miss what's happening.",
  },
  {
    chapter: "Do This Next",
    icon: "✅",
    path: "/portal",
    target: '[data-testid="next-steps-card"]',
    placement: "right",
    disableBeacon: true,
    title: "'What's next for you'",
    content:
      "This card always tells you the single next thing your specialist needs from you — no scavenger hunt, no reading between the lines.",
  },
  {
    chapter: "Do This Next",
    icon: "📎",
    path: "/portal",
    target: '[data-testid="upload-card"]',
    placement: "left",
    disableBeacon: true,
    title: "Upload requested documents here",
    content:
      "IDs, statements, credit reports — anything requested lands in this box. Everything is encrypted and only visible to your assigned specialist team.",
  },

  // ============ NEW ✨: WHAT'S NEW IN CREDIT QUACK ============
  {
    chapter: "What's New",
    icon: "📊",
    path: "/portal",
    target: '[data-testid="ticker-tape"]',
    placement: "bottom",
    disableBeacon: true,
    title: "Live Ticker Tape at the top",
    content:
      "That NYSE-style ribbon at the very top? It's your daily funding intel — AI-curated every night with alerts on APR windows, closing offers, big client funding wins, and fresh sub-8-figure programs so nothing catches you off guard.",
  },
  {
    chapter: "What's New",
    icon: "🛒",
    path: "/portal/services",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Start New Services — 279 offerings",
    content:
      "A full marketplace of everything we can do for you beyond disputes: business credit, funding consultations, LLC formation, DUNS registration, credit-report reviews, tradelines, personal funding plays, and more. Pick, add to cart, and we'll take it from there.",
  },
  {
    chapter: "What's New",
    icon: "🏦",
    path: "/portal/tradelines",
    target: '[data-testid="tl-learn-open"]',
    placement: "bottom",
    disableBeacon: true,
    title: "Buy Tradelines — with a built-in education center",
    content:
      "Aged authorized-user tradelines from vetted wholesale partners — real-time synced hourly, AI-priced fairly, and fully self-serve. New to tradelines? Hit the glowing 'What is a Tradeline?' button for a 60-second guide before you shop.",
  },
  {
    chapter: "What's New",
    icon: "🎯",
    path: "/portal/tradelines",
    target: '[data-testid="tl-quiz-open"]',
    placement: "bottom",
    disableBeacon: true,
    title: "Help Me Choose — AI-matched tradelines",
    content:
      "Not sure which line fits your funding goal or timing? Answer a 5-question quiz and we auto-filter tradelines that post before your target lender application — no guessing.",
  },
  {
    chapter: "What's New",
    icon: "🎁",
    path: "/portal/tradelines",
    target: '[data-testid="tl-promo-input"]',
    placement: "bottom",
    disableBeacon: true,
    title: "Promo codes — Friends & Family",
    content:
      "Have a code from your specialist? Drop it here for exclusive perks — Friends & Family unlocks 15% off every tradeline in your cart plus waives all rush fees. Gold shimmer, confetti, and instant savings on every card.",
  },
  {
    chapter: "What's New",
    icon: "💳",
    path: "/portal/tradelines/reservations",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Secure checkout with Stripe",
    content:
      "Reserve tradelines and pay instantly through Stripe's hosted checkout — bank-grade security, receipts emailed automatically, and your specialist gets notified the second payment clears. Non-refundable once processed, so review your cart before you confirm.",
  },
  {
    chapter: "What's New",
    icon: "🔔",
    path: "/portal/messages",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Real-time message pings",
    content:
      "Whenever your specialist replies, you'll hear a soft ping and see a red dot in the top-right — even if you're on another page. No more refreshing your inbox to check.",
  },

  // ============ CHAPTER 2: COMMUNICATION ============
  {
    chapter: "Talk to Your Team",
    icon: "💬",
    path: "/portal/messages",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Messages — your direct line",
    content:
      "This is a full two-way message thread with your assigned specialist. Every reply is logged so context never gets lost between calls. Photos and PDFs can be attached.",
  },
  {
    chapter: "Talk to Your Team",
    icon: "📅",
    path: "/portal/meetings",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Meetings — pick your time",
    content:
      "When your specialist proposes call times, they show up here. Tap one to confirm, add it to Google/Outlook Calendar, or ask for a different slot.",
  },

  // ============ CHAPTER 3: FUNDING CENTER — DEEP DIVE ============
  {
    chapter: "Funding Center",
    icon: "💰",
    path: "/portal/funding",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Welcome to the Funding Center",
    content:
      "The Funding Center is the payoff of this whole process. Once your credit is strong enough, we use everything we know about your profile to find real cards, LOCs, and lender products that actually fit — no spray-and-pray applications, no hard-pull surprises.",
  },
  {
    chapter: "Credit Report",
    icon: "🧠",
    path: "/portal/funding?section=my_report",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Step 1 — Drop your credit report PDF",
    content:
      "Grab a PDF from Credit Karma, IdentityIQ, Experian, or annualcreditreport.com and drop it here. Our AI reads it in seconds — no typing, no data entry.",
  },
  {
    chapter: "Credit Report",
    icon: "✨",
    path: "/portal/funding?section=my_report",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "What the AI extracts",
    content:
      "Every tradeline (open + closed), balances, utilization per card, hard inquiries with dates, public records, collections, and each bureau score. It structures everything so the Simulator + funding matches use your REAL numbers, not guesses.",
  },
  {
    chapter: "Credit Report",
    icon: "📈",
    path: "/portal/funding?section=my_report",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Score history + deltas",
    content:
      "Upload a fresh report every 30–45 days. We track score deltas per bureau (↑/↓ with the exact points gained/lost) and celebrate every win so you can literally see the progress.",
  },
  {
    chapter: "Simulator",
    icon: "🎛️",
    path: "/portal/funding?section=simulator",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "The Funding Simulator",
    content:
      "Play with the numbers: what if I pay this card down to 10%? What if my score jumps 40 points? What if I add a tradeline? The Simulator projects your approval odds live — no more mystery.",
  },
  {
    chapter: "Simulator",
    icon: "🧮",
    path: "/portal/funding?section=simulator",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "How the Simulator scores you",
    content:
      "It weighs utilization, score, inquiries, age of credit, and collection history — then matches you against the approval bands of real lenders our team has tracked. When we say 'you're likely approved', it's grounded in actual data.",
  },
  {
    chapter: "Matches",
    icon: "🎯",
    path: "/portal/funding?section=matches",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Your personal funding matches",
    content:
      "This is where the magic happens: cards, LOCs, and lenders filtered by your live profile. Each match shows likely approval odds, target credit line, hard/soft-pull tag, best time to apply, and how to apply strategically.",
  },
  {
    chapter: "Feed",
    icon: "📰",
    path: "/portal/feed",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "The Funding Feed",
    content:
      "The Credit Quack team curates fresh cards, LOCs, business funding, 0% APR windows, and lender picks — updated hourly. This is the leaderboard of what's working right now.",
  },

  // ============ CHAPTER 4: THE DISPUTE JOURNEY ============
  {
    chapter: "The Dispute Journey",
    icon: "⚖️",
    path: "/portal",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "How disputes actually work — step by step",
    content:
      "When you hit stage 3 ('Disputes Submitted'), we prepare and mail dispute packages to Experian, Equifax, TransUnion, the Better Business Bureau, and the Consumer Financial Protection Bureau. We use FCRA protections to challenge anything inaccurate, incomplete, or unverifiable.",
  },
  {
    chapter: "The Dispute Journey",
    icon: "⏳",
    path: "/portal",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Then we wait — but you help too",
    content:
      "Bureau Processing (stage 4) can take 14–30+ days. During this window, watch your physical mail, email, and credit-monitoring alerts. If ANY letter arrives from a bureau, creditor, collector, CFPB, or BBB — tell us via Messages immediately. Every reply matters.",
  },
  {
    chapter: "The Dispute Journey",
    icon: "🌟",
    path: "/portal",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Results → Optimization → Funding Ready",
    content:
      "Stage 5 (Results Coming In) is where responses land and we review outcomes. Stage 6 (Credit Optimization) is fine-tuning — knocking down utilization, timing, and lender fit. Stage 7 (Funding Ready) is the green light: your profile now meets the funding strategy.",
  },
  {
    chapter: "The Dispute Journey",
    icon: "🏁",
    path: "/portal",
    target: "body",
    placement: "center",
    disableBeacon: true,
    title: "Funding in Progress → Completed 🎉",
    content:
      "Stage 8 is when applications go out (strategically, not sprayed). Stage 9 is 'Completed' — one full Credit Quack cycle done, credit rebuilt, funding secured. Then we celebrate.",
  },

  // ============ CHAPTER 5: WRAP-UP ============
  {
    chapter: "Help & Restart",
    icon: "🆘",
    path: "/portal",
    target: '[data-testid="nav-walkthrough"]',
    placement: "right",
    disableBeacon: true,
    title: "Restart this tour anytime",
    content:
      "This orange button is always here in the sidebar. Come back anytime you want a refresher — or share with a friend and walk them through it.",
  },
  {
    chapter: "You're Ready",
    icon: "🚀",
    path: "/portal",
    target: '[data-testid="pizza-tracker"]',
    placement: "bottom",
    disableBeacon: true,
    title: "You're all set 🎉",
    content:
      "That's the full tour. If you ever get stuck, message your specialist directly. Now — go put your credit to work.",
  },
];

const CHAPTERS = [...new Set(STEPS.map((s) => s.chapter))];

/* ---------------------------------------------------------------- */
/* Bouncing arrow overlay — only when target isn't 'body'            */
/* ---------------------------------------------------------------- */
function BouncingArrow({ targetSelector }) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!targetSelector || targetSelector === "body") { setRect(null); return; }
    const compute = () => {
      const el = document.querySelector(targetSelector);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, w: r.width, h: r.height });
    };
    compute();
    const id = setInterval(compute, 250);
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      clearInterval(id);
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [targetSelector]);

  if (!rect) return null;
  const top = rect.top - 62;
  const left = rect.left + rect.w / 2 - 26;
  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: Math.max(4, top),
        left: Math.max(4, left),
        zIndex: 10001,
        pointerEvents: "none",
      }}
    >
      <div className="cq-tour-arrow">
        <svg width="52" height="60" viewBox="0 0 52 60" fill="none">
          <defs>
            <linearGradient id="cqTourGrad" x1="0" y1="0" x2="0" y2="60">
              <stop offset="0%" stopColor="#fb923c" />
              <stop offset="100%" stopColor="#f43f5e" />
            </linearGradient>
          </defs>
          <path d="M26 4 L26 40 M12 28 L26 44 L40 28"
            stroke="url(#cqTourGrad)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <circle cx="26" cy="52" r="3" fill="url(#cqTourGrad)" />
        </svg>
      </div>
      <style>{`
        .cq-tour-arrow { animation: cq-tour-bounce 1s ease-in-out infinite; filter: drop-shadow(0 6px 12px rgba(251,146,60,0.35)); }
        @keyframes cq-tour-bounce { 0%,100% { transform: translateY(0);} 50% { transform: translateY(-14px);} }
      `}</style>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Custom tooltip card                                                */
/* ---------------------------------------------------------------- */
function TourTooltip({ index, size, step, backProps, primaryProps, skipProps, tooltipProps, isLastStep }) {
  const chapterIdx = CHAPTERS.indexOf(step.chapter);
  // Local UI state — reads from localStorage so it persists across tours
  const [muted, setMuted] = React.useState(() => _isMuted());
  const [voiceOn, setVoiceOn] = React.useState(() => _isVoiceEnabled());

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    try { localStorage.setItem(MUTE_KEY, next ? "1" : "0"); } catch {}
    if (next) stopSpeaking();
  };
  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    try { localStorage.setItem(VOICE_KEY, next ? "1" : "0"); } catch {}
    if (next && !muted) {
      // Start speaking the current step immediately when the user enables voice
      speakStep(step);
      // Also warm the next step's audio
      const nextStep = STEPS[index + 1];
      if (nextStep) prefetchStep(nextStep);
    } else {
      stopSpeaking();
    }
  };

  return (
    <div
      {...tooltipProps}
      className="max-w-md rounded-2xl bg-white shadow-2xl border border-amber-200 overflow-hidden"
      style={{ boxShadow: "0 25px 50px -12px rgba(251,146,60,0.35), 0 0 0 4px rgba(251,146,60,0.15)" }}
    >
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 h-1.5" />
      <div className="p-5">
        {/* Chapter + step counter + audio controls */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl leading-none" aria-hidden>{step.icon}</span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-bold text-amber-600 truncate">
              Chapter {chapterIdx + 1} · {step.chapter}
            </p>
            <p className="text-[10px] font-semibold text-slate-400">Step {index + 1} of {size}</p>
          </div>

          {/* Voice-over toggle */}
          <button
            type="button"
            onClick={toggleVoice}
            className={`rounded-lg p-1.5 transition border ${voiceOn && !muted ? "bg-amber-500 text-white border-amber-500 shadow-sm" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
            title={voiceOn ? "Turn off narrator" : "Have the walkthrough read aloud"}
            aria-label={voiceOn ? "Turn off narrator" : "Turn on narrator"}
            aria-pressed={voiceOn}
            data-testid="walkthrough-voice-toggle"
          >
            {voiceOn ? <Mic size={14} /> : <MicOff size={14} />}
          </button>
          {/* Master mute */}
          <button
            type="button"
            onClick={toggleMute}
            className={`rounded-lg p-1.5 transition border ${muted ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
            title={muted ? "Un-mute all sounds" : "Mute all sounds (quacks, celebration, narrator)"}
            aria-label={muted ? "Un-mute" : "Mute all sounds"}
            aria-pressed={muted}
            data-testid="walkthrough-mute-toggle"
          >
            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          </button>
        </div>

        {/* Progress bar */}
        <div className="flex gap-0.5 mb-3">
          {Array.from({ length: size }).map((_, i) => (
            <span
              key={i}
              className={`h-1 flex-1 rounded-full transition ${i <= index ? "bg-amber-500" : "bg-slate-200"}`}
            />
          ))}
        </div>

        <h4 className="font-display font-black text-slate-900 text-xl leading-tight">{step.title}</h4>
        <p className="text-slate-600 mt-2 text-sm leading-relaxed">{step.content}</p>

        <div className="flex items-center gap-2 mt-4">
          <button
            {...skipProps}
            className="text-xs text-slate-400 hover:text-slate-600 transition"
            data-testid="walkthrough-skip"
          >
            Skip tour
          </button>
          <div className="ml-auto flex items-center gap-2">
            {index > 0 && (
              <button
                {...backProps}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100"
                data-testid="walkthrough-back"
              >
                Back
              </button>
            )}
            <button
              {...primaryProps}
              className="px-4 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-md shadow-amber-200 transition"
              data-testid="walkthrough-next"
            >
              {isLastStep ? "Finish 🎉" : "Got it → Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function celebrate() {
  const shoot = (angle, x) => confetti({
    particleCount: 80, spread: 65, startVelocity: 55, origin: { x, y: 0.65 }, angle,
    colors: ["#f59e0b", "#f97316", "#fb923c", "#f43f5e", "#facc15", "#34d399", "#a78bfa"],
  });
  shoot(60, 0.15); shoot(90, 0.5); shoot(120, 0.85);
  setTimeout(() => shoot(90, 0.5), 220);
  setTimeout(() => confetti({ particleCount: 160, spread: 110, origin: { y: 0.6 }, scalar: 1.3 }), 480);
  // Extended confetti finale — bigger, wider, longer
  setTimeout(() => {
    const duration = 1500;
    const end = Date.now() + duration;
    (function frame() {
      confetti({ particleCount: 6, angle: 60, spread: 55, origin: { x: 0 }, colors: ["#f59e0b", "#f97316"] });
      confetti({ particleCount: 6, angle: 120, spread: 55, origin: { x: 1 }, colors: ["#f43f5e", "#a78bfa"] });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, 900);
}

/* ---------------------------------------------------------------- */
/* Sound effects — real MP3s for the duck quack + celebration horn.  */
/* If MP3 playback is blocked (autoplay policy, network hiccup) we    */
/* fall back to a Web-Audio synth so the tour is never silent.        */
/* ---------------------------------------------------------------- */
function _audioCtx() {
  try {
    if (typeof window === "undefined") return null;
    if (!window.__cqAudioCtx) {
      const C = window.AudioContext || window.webkitAudioContext;
      if (!C) return null;
      window.__cqAudioCtx = new C();
    }
    if (window.__cqAudioCtx.state === "suspended") {
      window.__cqAudioCtx.resume().catch(() => {});
    }
    return window.__cqAudioCtx;
  } catch { return null; }
}

/** Pre-decode an MP3 into an AudioBuffer so playback is instant. */
async function _loadBuffer(url) {
  const a = _audioCtx();
  if (!a) return null;
  if (!window.__cqSfxCache) window.__cqSfxCache = {};
  if (window.__cqSfxCache[url]) return window.__cqSfxCache[url];
  try {
    const res = await fetch(url, { cache: "force-cache" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const ab = await res.arrayBuffer();
    const buf = await a.decodeAudioData(ab);
    window.__cqSfxCache[url] = buf;
    return buf;
  } catch { return null; }
}

function _playBuffer(buf, gain = 0.55) {
  const a = _audioCtx();
  if (!a || !buf) return false;
  try {
    const src = a.createBufferSource();
    src.buffer = buf;
    const g = a.createGain();
    g.gain.value = gain;
    src.connect(g); g.connect(a.destination);
    src.start(0);
    return true;
  } catch { return false; }
}

/** Fallback synth (only if MP3 fails to load) — 2-syllable quack. */
function _synthQuack() {
  const a = _audioCtx();
  if (!a) return;
  try {
    const now = a.currentTime;
    const drop = (t0, from, to, dur, gain = 0.22) => {
      const o = a.createOscillator();
      const g = a.createGain();
      const filter = a.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = 900; filter.Q.value = 4;
      o.type = "sawtooth";
      o.frequency.setValueAtTime(from, t0);
      o.frequency.exponentialRampToValueAtTime(to, t0 + dur);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(gain, t0 + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      o.connect(filter); filter.connect(g); g.connect(a.destination);
      o.start(t0); o.stop(t0 + dur + 0.02);
    };
    drop(now, 620, 240, 0.09);
    drop(now + 0.08, 540, 200, 0.14);
  } catch { /* silent */ }
}

async function playQuack() {
  if (_isMuted()) return;
  const buf = await _loadBuffer("/sfx/duck-quack.mp3");
  if (!_playBuffer(buf, 0.6)) _synthQuack();
}

async function playFanfare() {
  if (_isMuted()) return;
  const buf = await _loadBuffer("/sfx/celebration.mp3");
  if (_playBuffer(buf, 0.55)) return;
  // Fallback: ascending arpeggio
  const a = _audioCtx();
  if (!a) return;
  try {
    const now = a.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, i) => {
      const t = now + i * 0.09;
      const o = a.createOscillator();
      const g = a.createGain();
      o.type = "triangle";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.32);
      o.connect(g); g.connect(a.destination);
      o.start(t); o.stop(t + 0.34);
    });
  } catch { /* silent */ }
}

/* ---------------------------------------------------------------- */
/* Voice-over — browser SpeechSynthesis with best available female  */
/* voice picked heuristically (no API key, works offline).           */
/* ---------------------------------------------------------------- */
function _isMuted() {
  try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
}
function _isVoiceEnabled() {
  // Voice-over is ON BY DEFAULT — users can turn it off from the tooltip.
  try {
    const v = localStorage.getItem(VOICE_KEY);
    if (v === null) return true;   // never set — default on
    return v === "1";
  } catch { return true; }
}

// Preferred female voices by name (most natural-sounding first)
const PREFERRED_VOICE_NAMES = [
  "Google US English",
  "Samantha",
  "Microsoft Aria Online (Natural)",
  "Microsoft Jenny Online (Natural)",
  "Microsoft Zira",
  "Microsoft Aria",
  "Karen",
  "Serena",
  "Moira",
  "Google UK English Female",
];

function _pickVoice() {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices || !voices.length) return null;
  // 1. Try preferred names by exact match
  for (const wanted of PREFERRED_VOICE_NAMES) {
    const v = voices.find((x) => x.name === wanted);
    if (v) return v;
  }
  // 2. Any voice whose name contains 'female' + lang starts with en
  const femaleEn = voices.find((v) => /female/i.test(v.name) && /^en/i.test(v.lang));
  if (femaleEn) return femaleEn;
  // 3. Any en-US voice
  const anyEnUS = voices.find((v) => v.lang === "en-US");
  if (anyEnUS) return anyEnUS;
  // 4. First voice
  return voices[0];
}

function stopSpeaking() {
  try { window.speechSynthesis?.cancel(); } catch { /* no-op */ }
  try {
    if (window.__cqTtsAbort) { window.__cqTtsAbort.abort(); window.__cqTtsAbort = null; }
  } catch { /* no-op */ }
  try {
    const el = window.__cqTtsAudio;
    if (el) { el.pause(); el.src = ""; window.__cqTtsAudio = null; }
  } catch { /* no-op */ }
}

// Monotonic counter used to discard stale TTS responses.
// Incremented on every speakStep() call and on every stopSpeaking().
if (typeof window !== "undefined" && window.__cqTtsGen === undefined) {
  window.__cqTtsGen = 0;
}

// Cache: text -> resolved audio URL (never re-fetch the same step within a session)
if (typeof window !== "undefined" && !window.__cqTtsUrlCache) {
  window.__cqTtsUrlCache = new Map();
}

async function _resolveTtsUrl(text, signal) {
  if (window.__cqTtsUrlCache.has(text)) return window.__cqTtsUrlCache.get(text);
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const token = (() => { try { return localStorage.getItem("cq_token"); } catch { return null; } })();
  const res = await fetch(`${base}/api/walkthrough/tts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ text, voice: "nova" }),
    signal,
  });
  if (!res.ok) throw new Error(`TTS ${res.status}`);
  const data = await res.json();
  if (!data?.url) throw new Error("No url");
  const fullUrl = `${base}${data.url}`;
  window.__cqTtsUrlCache.set(text, fullUrl);
  return fullUrl;
}

/**
 * Speak a step's title + content using OpenAI TTS (tts-1-hd + nova voice).
 * Race-safe: uses a monotonic generation counter to discard stale responses
 * so rapid Back/Next clicks never overlap or play the wrong step.
 */
async function speakStep(step) {
  if (!step) return;
  if (_isMuted() || !_isVoiceEnabled()) return;

  // Bump generation + cancel any previous in-flight fetch or playback
  const gen = ++window.__cqTtsGen;
  stopSpeaking();
  window.__cqTtsGen = gen; // stopSpeaking abort restores; keep our claim

  const controller = new AbortController();
  window.__cqTtsAbort = controller;

  const text = `${step.title}. ${step.content}`;

  try {
    const audioUrl = await _resolveTtsUrl(text, controller.signal);

    // Guard: another step took over, or user muted/disabled voice
    if (gen !== window.__cqTtsGen) return;
    if (_isMuted() || !_isVoiceEnabled()) return;

    const audio = new Audio(audioUrl);
    audio.preload = "auto";
    audio.volume = 1.0;
    window.__cqTtsAudio = audio;

    await audio.play();
    return;
  } catch (err) {
    if (err?.name === "AbortError") return;      // superseded — stay silent
    if (gen !== window.__cqTtsGen) return;       // stale
    // Fall through to SpeechSynthesis fallback
  }

  // Fallback — browser voice (only when cloud TTS failed AND we're still current)
  if (gen !== window.__cqTtsGen) return;
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const v = _pickVoice();
    if (v) u.voice = v;
    u.rate = 1.0;
    u.pitch = 1.05;
    u.volume = 1.0;
    u.lang = (v && v.lang) || "en-US";
    window.speechSynthesis.speak(u);
  } catch { /* silent */ }
}

/**
 * Warm the URL cache for a step without playing anything — fires the POST
 * so the audio is ready the instant the user clicks Next.
 */
function prefetchStep(step) {
  if (!step || _isMuted() || !_isVoiceEnabled()) return;
  const text = `${step.title}. ${step.content}`;
  if (window.__cqTtsUrlCache.has(text)) return;
  _resolveTtsUrl(text).catch(() => { /* silent */ });
}

export default function WalkthroughTour() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const bootedRef = useRef(false);
  const autoTimerRef = useRef(null);
  const pausedForIntakeRef = useRef(false);
  const navTimerRef = useRef(null);   // pending cross-route step advance
  const stepGenRef = useRef(0);       // monotonic guard for stale advances

  const storageKey = user?.id ? `${STORAGE_KEY_PREFIX}${user.id}` : null;

  const start = useCallback(() => {
    // Voice is ON BY DEFAULT — persist that so the tooltip toggle reflects it.
    try {
      if (localStorage.getItem(VOICE_KEY) === null) localStorage.setItem(VOICE_KEY, "1");
    } catch { /* no-op */ }
    // Do NOT auto-navigate — that caused the previous "bounce back to /portal" loop.
    // We simply open the tour with stepIndex 0 wherever the user currently is.
    setStepIndex(0);
    setRun(true);
    // Warm the audio cache so the first quack fires without a fetch delay.
    _loadBuffer("/sfx/duck-quack.mp3");
    _loadBuffer("/sfx/celebration.mp3");
    // Prime speechSynthesis voices (Chrome loads them lazily)
    try { window.speechSynthesis?.getVoices(); } catch {}
  }, []);

  useEffect(() => {
    const h = () => start();
    window.addEventListener(RUNNING_EVENT, h);
    // Prospect intake pause/resume — the ProspectIntakeModal fires these so
    // the walkthrough waits until the client finishes their onboarding modal.
    const pause = () => {
      pausedForIntakeRef.current = true;
      // Cancel any queued auto-launch and force-close if joyride already started.
      if (autoTimerRef.current) { clearTimeout(autoTimerRef.current); autoTimerRef.current = null; }
      setRun(false);
    };
    const resume = () => {
      pausedForIntakeRef.current = false;
      if (user?.role === "client" && storageKey && localStorage.getItem(storageKey) !== "1") {
        setTimeout(() => start(), 400);
      }
    };
    window.addEventListener("cq:walkthrough:pause", pause);
    window.addEventListener("cq:walkthrough:resume", resume);
    return () => {
      window.removeEventListener(RUNNING_EVENT, h);
      window.removeEventListener("cq:walkthrough:pause", pause);
      window.removeEventListener("cq:walkthrough:resume", resume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, user?.id, storageKey]);

  // Auto-launch once per client. Persist the "seen" flag ONLY when the tour
  // actually starts (not upfront) — that way if the prospect intake modal
  // resumes us later, we can still auto-launch.
  useEffect(() => {
    if (!user || user.role !== "client" || !storageKey) return;
    if (loc.pathname.startsWith("/city")) return; // never over the game
    if (bootedRef.current) return;
    if (localStorage.getItem(storageKey) === "1") return;
    bootedRef.current = true;
    if (pausedForIntakeRef.current) return; // wait for resume
    autoTimerRef.current = setTimeout(() => {
      autoTimerRef.current = null;
      if (pausedForIntakeRef.current) return; // late-mounted modal
      try { localStorage.setItem(storageKey, "1"); } catch {}
      start();
    }, 1800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, storageKey, loc.pathname]);

  const finish = useCallback((withConfetti) => {
    setRun(false);
    stopSpeaking();
    if (storageKey) localStorage.setItem(storageKey, "1");
    if (withConfetti) { celebrate(); playFanfare(); }
  }, [storageKey]);

  const handleEvent = useCallback((data) => {
    const { action, index, status, type } = data;

    if (status === STATUS.FINISHED) { finish(true); return; }
    if (status === STATUS.SKIPPED)  { finish(false); return; }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const isPrev = action === ACTIONS.PREV;
      const nextIdx = isPrev ? Math.max(0, index - 1) : index + 1;
      if (nextIdx >= STEPS.length) { finish(true); return; }

      // Cancel any pending cross-route step advance from the previous click so
      // rapid Next/Back bursts don't stack setStepIndex writes out of order.
      if (navTimerRef.current) {
        clearTimeout(navTimerRef.current);
        navTimerRef.current = null;
      }
      const myGen = ++stepGenRef.current;

      // Quack on every forward step; silent on Back
      if (!isPrev) playQuack();
      const nextStep = STEPS[nextIdx];
      const currentPath = loc.pathname + (loc.search || "");
      if (nextStep?.path && currentPath !== nextStep.path) {
        nav(nextStep.path);
        navTimerRef.current = setTimeout(() => {
          navTimerRef.current = null;
          if (myGen !== stepGenRef.current) return;     // superseded
          setStepIndex(nextIdx);
        }, 400);
      } else {
        setStepIndex(nextIdx);
      }
    }
  }, [finish, loc.pathname, loc.search, nav]);

  // Clear any pending nav timeout on unmount
  useEffect(() => () => {
    if (navTimerRef.current) { clearTimeout(navTimerRef.current); navTimerRef.current = null; }
  }, []);

  // Speak the current step whenever the tour advances (voice toggle enabled).
  // Also prefetch the NEXT step's audio so the click-to-hear latency stays near-zero.
  useEffect(() => {
    if (!run) { stopSpeaking(); return; }
    const step = STEPS[stepIndex];
    if (!step) return;
    speakStep(step);
    // Warm the next step's URL in parallel
    const next = STEPS[stepIndex + 1];
    if (next) prefetchStep(next);
    return () => { stopSpeaking(); };
  }, [run, stepIndex]);

  // Stop speaking if the component ever unmounts
  useEffect(() => () => stopSpeaking(), []);

  const currentStep = STEPS[stepIndex];
  const currentTarget = currentStep?.target;

  if (!user || user.role !== "client") return null;

  return (
    <>
      <Joyride
        steps={STEPS}
        run={run}
        stepIndex={stepIndex}
        continuous
        tooltipComponent={TourTooltip}
        onEvent={handleEvent}
        options={{
          zIndex: 10000,
          overlayColor: "rgba(15, 23, 42, 0.55)",
          primaryColor: "#f59e0b",
          spotlightPadding: 8,
          showSkipButton: true,
          disableOverlayClose: true,
        }}
      />
      {run && currentTarget && currentTarget !== "body" && (
        <BouncingArrow targetSelector={currentTarget} />
      )}
    </>
  );
}

/* Named helper any component can call to launch the tour */
export function launchWalkthrough() {
  window.dispatchEvent(new CustomEvent(RUNNING_EVENT));
}
